/**
 * Between-floor roguelike upgrade draft — the same system zombies uses
 * (rollDraft + applyUpgrade + a weighted registry of stacking upgrades), adapted
 * for a top-down tank. Every floor cleared, the player drafts one of three random
 * upgrades; picks stack (multiplicatively for "%": levers, additively for counts)
 * and are recorded so a resumed run can replay them.
 */

import { BULLET, MINE } from "../config";

export type UpgradeSymbol = "triangle" | "bolt" | "chevron" | "square" | "plus" | "diamond";

export interface UpgradeDef {
  id: string;
  name: string;
  desc: string;
  color: number; // suit colour (card header + HUD pip)
  symbol: UpgradeSymbol;
  weight: number; // relative draw odds
  apply: (ctx: UpgradeContext) => void;
}

/** The run-scoped levers an upgrade mutates. Reset to defaults each fresh run. */
export interface UpgradeLevers {
  shellSpeedMul: number;
  cooldownMul: number; // lower = faster (clamped)
  maxShells: number;
  maxBounces: number;
  maxMines: number;
  speedMul: number;
  turnSpeedMul: number;
  shieldHits: number; // 1-hit shields granted at the start of each floor
  damage: number; // shield/health points each shell removes (Punch); 1 = normal
  mineRadiusMul: number; // player mine blast-radius (+ visual) multiplier (Heavy Mines)
  shellHp: number; // how many incoming bullets ONE player shell can shoot down before it's spent (Dense Rounds); 1 = normal
}

export function defaultLevers(): UpgradeLevers {
  return {
    shellSpeedMul: 1,
    cooldownMul: 1,
    maxShells: BULLET.maxPlayerShells,
    maxBounces: BULLET.maxBounces,
    maxMines: MINE.maxPlayer,
    speedMul: 1,
    turnSpeedMul: 1,
    shieldHits: 0,
    damage: 1,
    mineRadiusMul: 1,
    shellHp: 1,
  };
}

export interface UpgradeContext {
  up: UpgradeLevers;
  /** grant one extra life immediately (raises the max too) */
  addLife: () => void;
}

export const UPGRADES: Record<string, UpgradeDef> = {
  velocity:   { id: "velocity",   name: "Velocity",    desc: "+7.5% shell speed",  color: 0x3fa9f5, symbol: "bolt",     weight: 9, apply: (c) => { c.up.shellSpeedMul *= 1.075; } },
  rapidbore:  { id: "rapidbore",  name: "Rapid Bore",  desc: "−7.5% fire cooldown", color: 0xf5d142, symbol: "bolt",     weight: 7, apply: (c) => { c.up.cooldownMul = Math.max(0.35, c.up.cooldownMul * 0.925); } },
  overload:   { id: "overload",   name: "Overload",    desc: "+1 shell on field",  color: 0xf5a623, symbol: "square",   weight: 6, apply: (c) => { c.up.maxShells += 1; } },
  ricochet:   { id: "ricochet",   name: "Ricochet",    desc: "+1 shell bounce",    color: 0x9b59b6, symbol: "chevron",  weight: 6, apply: (c) => { c.up.maxBounces = Math.min(4, c.up.maxBounces + 1); } },
  sapper:     { id: "sapper",     name: "Sapper",      desc: "+1 mine",            color: 0xe74c3c, symbol: "diamond",  weight: 6, apply: (c) => { c.up.maxMines += 1; } },
  treads:     { id: "treads",     name: "Treads",      desc: "+4% move speed",     color: 0x2ecc71, symbol: "chevron",  weight: 9, apply: (c) => { c.up.speedMul *= 1.04; } },
  gyro:       { id: "gyro",       name: "Gyro",        desc: "+6% turn speed",     color: 0x1abc9c, symbol: "triangle", weight: 9, apply: (c) => { c.up.turnSpeedMul *= 1.06; } },
  bulwark:    { id: "bulwark",    name: "Bulwark",     desc: "+1 shield each floor", color: 0x5dade2, symbol: "plus",   weight: 5, apply: (c) => { c.up.shieldHits += 1; } },
  punch:      { id: "punch",      name: "Punch",       desc: "+1 shell damage",     color: 0xff4444, symbol: "diamond", weight: 6, apply: (c) => { c.up.damage += 1; } },
  bigmines:   { id: "bigmines",   name: "Heavy Mines", desc: "+25% mine blast radius", color: 0xe67e22, symbol: "diamond", weight: 5, apply: (c) => { c.up.mineRadiusMul *= 1.25; } },
  density:    { id: "density",    name: "Dense Rounds", desc: "+1 bullet downed per shell", color: 0x95a5a6, symbol: "square", weight: 6, apply: (c) => { c.up.shellHp += 1; } },
  spareparts: { id: "spareparts", name: "Spare Parts", desc: "+1 life",            color: 0xff6b9d, symbol: "plus",     weight: 5, apply: (c) => { c.addLife(); } },
};

export const UPGRADE_ORDER = Object.keys(UPGRADES);

/** Draw `n` DISTINCT upgrades, weighted by their draw odds. */
export function rollDraft(n = 3): UpgradeDef[] {
  const pool = UPGRADE_ORDER.map((id) => UPGRADES[id]);
  const out: UpgradeDef[] = [];
  while (out.length < n && pool.length > 0) {
    let total = 0;
    for (const u of pool) total += u.weight;
    let r = Math.random() * total;
    let idx = 0;
    for (let i = 0; i < pool.length; i++) {
      r -= pool[i].weight;
      if (r <= 0) { idx = i; break; }
    }
    out.push(pool.splice(idx, 1)[0]);
  }
  return out;
}

/** Apply one upgrade by id (used for picks AND for replaying a resumed run). */
export function applyUpgrade(id: string, ctx: UpgradeContext): boolean {
  const def = UPGRADES[id];
  if (!def) return false;
  def.apply(ctx);
  return true;
}

/** Inline SVG glyph for a symbol (used on draft cards + HUD pips). */
export function symbolSvg(symbol: UpgradeSymbol, color = "#fff"): string {
  const paths: Record<UpgradeSymbol, string> = {
    triangle: "M12 4 L20 19 L4 19 Z",
    bolt: "M13 2 L4 14 L11 14 L9 22 L20 9 L13 9 Z",
    chevron: "M5 8 L12 14 L19 8 L19 12 L12 18 L5 12 Z",
    square: "M5 5 H19 V19 H5 Z",
    plus: "M10 4 H14 V10 H20 V14 H14 V20 H10 V14 H4 V10 H10 Z",
    diamond: "M12 3 L21 12 L12 21 L3 12 Z",
  };
  return `<svg viewBox="0 0 24 24" width="100%" height="100%"><path d="${paths[symbol]}" fill="${color}"/></svg>`;
}
